import * as React from "react"
import { Check, ChevronsUpDown, X, Loader2, GripVertical } from "lucide-react"
import { useDebounce } from "use-debounce"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

import { cn } from "@/lib/utils"
import { useIsReadOnly } from "@/contexts/ReadOnlyContext"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Badge } from "@/components/ui/badge"

export interface MultiSelectComboboxOption {
  value: string
  label: string
  description?: string
  disabled?: boolean
}

// SortableBadge component for drag-to-reorder
interface SortableBadgeProps {
  option: MultiSelectComboboxOption
  onRemove: (value: string, e: React.MouseEvent) => void
}

function SortableBadge({ option, onRemove }: SortableBadgeProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.value })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <Badge
      ref={setNodeRef}
      style={style}
      variant="secondary"
      className="bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200 border border-transparent hover:bg-gray-200 hover:border-gray-400 dark:hover:bg-gray-600 dark:hover:border-gray-500 max-w-full"
      title={option.label}
    >
      <span
        {...attributes}
        {...listeners}
        className="mr-1 cursor-grab active:cursor-grabbing shrink-0"
      >
        <GripVertical className="h-3 w-3 text-gray-700 dark:text-gray-200" />
      </span>
      <span className="truncate">{option.label}</span>
      <span
        className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer inline-flex items-center shrink-0"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            e.stopPropagation()
            onRemove(option.value, e as any)
          }
        }}
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onRemove(option.value, e as any)
        }}
      >
        <X className="h-3 w-3 text-gray-700 dark:text-gray-200" />
      </span>
    </Badge>
  )
}

interface MultiSelectComboboxProps {
  options: MultiSelectComboboxOption[]
  value: string[]
  onValueChange?: (values: string[]) => void
  placeholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  onInputChange?: (query: string) => void
  isLoading?: boolean
  dragToReorder?: boolean
}

export function MultiSelectCombobox({
  options,
  value = [],
  onValueChange,
  placeholder = "Select options...",
  emptyText = "No options found.",
  disabled,
  className,
  onInputChange,
  isLoading = false,
  dragToReorder = false,
}: MultiSelectComboboxProps) {
  const inReadOnlyRegion = useIsReadOnly()
  const isDisabled = disabled ?? inReadOnlyRegion
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const [debouncedInputValue] = useDebounce(inputValue, 500)

  // Set up drag and drop sensors (only when dragToReorder is enabled)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  React.useEffect(() => {
    if (onInputChange) {
      onInputChange(debouncedInputValue)
    }
  }, [debouncedInputValue, onInputChange])

  // Maintain order based on value array order (important for drag-to-reorder).
  // Selected values missing from `options` (e.g. truncated server-side option
  // lists or active search queries) still render, using the raw value as label.
  const selectedOptions = React.useMemo(() => {
    return value.map(
      (val) => options.find((opt) => opt.value === val) ?? { value: val, label: val }
    )
  }, [options, value])

  const handleSelect = React.useCallback(
    (optionValue: string) => {
      const newValues = value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue]
      onValueChange?.(newValues)
    },
    [value, onValueChange]
  )

  const handleRemove = React.useCallback(
    (optionValue: string, e: React.MouseEvent) => {
      e.stopPropagation()
      const newValues = value.filter((v) => v !== optionValue)
      onValueChange?.(newValues)
    },
    [value, onValueChange]
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = value.indexOf(active.id as string)
      const newIndex = value.indexOf(over.id as string)
      const newValues = arrayMove(value, oldIndex, newIndex)
      onValueChange?.(newValues)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between min-h-9 h-auto py-1.5 overflow-hidden", className)}
          disabled={isDisabled}
        >
          <div className="flex flex-wrap gap-1 flex-1 items-center min-w-0 overflow-hidden">
            {isLoading && selectedOptions.length === 0 ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                <span className="text-muted-foreground">Loading values...</span>
              </>
            ) : selectedOptions.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : dragToReorder ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={value}
                  strategy={verticalListSortingStrategy}
                >
                  {selectedOptions.map((option) => (
                    <SortableBadge
                      key={option.value}
                      option={option}
                      onRemove={handleRemove}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              selectedOptions.map((option) => (
                <Badge
                  key={option.value}
                  variant="secondary"
                  className="bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200 border border-transparent hover:bg-gray-200 hover:border-gray-400 dark:hover:bg-gray-600 dark:hover:border-gray-500 max-w-full"
                  title={option.label}
                >
                  <span className="truncate">{option.label}</span>
                  <span
                    className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer inline-flex items-center shrink-0"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        e.stopPropagation()
                        handleRemove(option.value, e as any)
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleRemove(option.value, e as any)
                    }}
                  >
                    <X className="h-3 w-3 text-gray-700 dark:text-gray-200" />
                  </span>
                </Badge>
              ))
            )}
          </div>
          {isLoading && selectedOptions.length > 0 ? (
            <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin opacity-50" />
          ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement
          if (target.closest('[cmdk-list]') || target.closest('[cmdk-item]')) {
            e.preventDefault()
          }
        }}
      >
        <Command shouldFilter={!onInputChange}>
          <CommandInput
            placeholder="Search..."
            value={inputValue}
            onValueChange={setInputValue}
          />
          <CommandList>
            {isLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Loading...
              </div>
            ) : (
              <>
                <CommandEmpty>{emptyText}</CommandEmpty>
                <CommandGroup>
                  {options.map((option) => {
                    const isSelected = value.includes(option.value)
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.label}
                        disabled={option.disabled}
                        onSelect={() => {
                          handleSelect(option.value)
                        }}
                        className="cursor-pointer"
                      >
                        <div className="mr-2 h-4 w-4 shrink-0 flex items-center justify-center">
                          <Check
                            className={cn(
                              "h-4 w-4",
                              isSelected ? "opacity-100" : "opacity-0"
                            )}
                          />
                        </div>
                        <div className="flex flex-col">
                          <span
                            className={cn(
                              "font-semibold",
                              option.description && "text-sm"
                            )}
                          >
                            {option.label}
                          </span>
                          {option.description && (
                            <span className="text-xs text-muted-foreground mt-0.5">
                              {option.description}
                          </span>
                          )}
                        </div>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

